import { useMemo } from 'react';

import { EMPTY_STRING } from '@e-socna/empty';
import type { FileCategoryId } from '@e-socna/data-layer';

import type { ReferencesContext_PrintStyle } from '../../../contexts/ReferencesContext';
import type { PrintGroup } from '../../../components/DocumentEditor';

import type { PrinterFormValues } from '../types/printer-form-values.type';

interface Params {
  readonly sectionTitles: readonly string[];
  readonly printStyle: ReferencesContext_PrintStyle;
  readonly fileCategoryId: FileCategoryId;
  readonly code?: string | null;
  readonly title?: string | null;
  readonly pdfPrintsIncrement: number;
  readonly contentToPrint: readonly PrintGroup[];
  readonly addFooters: boolean;
  readonly addHeaders: boolean;
  readonly needsPrintFeedbacks: boolean;
}

export const useInitialValuesFactory = ({
  sectionTitles,
  printStyle,
  fileCategoryId,
  code,
  title,
  pdfPrintsIncrement,
  contentToPrint,
  addFooters,
  addHeaders,
  needsPrintFeedbacks,
}: Readonly<Params>) => {
  const sectionTitlesList = JSON.stringify(sectionTitles);

  const printGroupsCount = contentToPrint.length;

  const initialValues: PrinterFormValues = useMemo(
    () => ({
      step: '0',
      printGroupsCount,
      sectionTitlesList,
      currentGroupIndex: '0',
      sectionDisplayFirstPageNumbers: EMPTY_STRING,
      addFooters,
      addHeaders,
      styleId: printStyle._id,
      styleCode: printStyle.code,
      fileCategoryId,
      code: code ?? EMPTY_STRING,
      title: title ?? EMPTY_STRING,
      needsPrintFeedbacks,
      pdfPrintsIncrement: pdfPrintsIncrement + 1,
      totalDisplayPageCount: EMPTY_STRING,
    }),
    [
      addFooters,
      addHeaders,
      code,
      fileCategoryId,
      needsPrintFeedbacks,
      pdfPrintsIncrement,
      printGroupsCount,
      printStyle._id,
      printStyle.code,
      sectionTitlesList,
      title,
    ],
  );

  return { initialValues };
};
